import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, CheckCircle, Clock, AlertCircle, Sparkle, Lightbulb } from 'lucide-react';
import { cn } from '@/lib/utils';
import IntegrationIcon from '../integrations/shared/IntegrationIcon';
import { useTranslation } from 'react-i18next';
import { HoverLift, GradientShift, PulseGlow } from '../ui/motion-primitives';

// Types
interface Integration {
  id: string;
  name: string;
  icon: string;
  description: string;
  category: IntegrationCategory;
  status: IntegrationStatus;
  
  // Navigation
  landingPath?: string;
  setupUrl?: string;
  
  // Metadata
  isNew?: boolean;
  isFeatured?: boolean;
  difficulty?: 'easy' | 'medium' | 'advanced';
  
  // Status Details
  releaseDate?: string;
  estimatedRelease?: string;
  progressPercentage?: number;
}

type IntegrationCategory = 
  | 'aiAssistants' 
  | 'communication' 
  | 'automation' 
  | 'development' 
  | 'productivity' 
  | 'socialMedia' 
  | 'browser' 
  | 'mobile';

type IntegrationStatus = 
  | 'ready' 
  | 'coming' 
  | 'development' 
  | 'concept' 
  | 'beta';

const STATUS_ORDER: Record<IntegrationStatus, number> = {
  ready: 0,
  beta: 1,
  development: 2,
  coming: 3,
  concept: 4,
};

function sortIntegrationsByStatus(integrations: Integration[]): Integration[] {
  return [...integrations].sort((a, b) => {
    const statusDiff = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
    if (statusDiff !== 0) return statusDiff;
    return a.name.localeCompare(b.name);
  });
}

const GRID_COLUMN_CLASS_MAP: Record<string, string> = {
  '1-1-1': 'grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1',
  '1-2-2': 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2',
  '1-2-3': 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  '2-2-2': 'grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2',
  '2-2-3': 'grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3',
  '2-3-3': 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3',
  '2-3-4': 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
};

function getGridColumnClasses(
  columns: { desktop: number; tablet: number; mobile: number },
  variant: 'homepage' | 'marketplace' | 'dashboard'
): string {
  const key = `${columns.mobile}-${columns.tablet}-${columns.desktop}`;
  const columnClasses =
    GRID_COLUMN_CLASS_MAP[key] ?? 'grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3';
  const gapClasses =
    variant === 'dashboard'
      ? 'gap-2 md:gap-3'
      : variant === 'marketplace'
        ? 'gap-3 md:gap-4'
        : 'gap-3 md:gap-6';
  return `${columnClasses} ${gapClasses}`;
}

interface IntegrationsGridProps {
  // Data Props
  integrations?: Integration[];
  maxDisplayed?: number;
  showAll?: boolean;
  showCategories?: boolean;
  enableFiltering?: boolean;
  
  // Display Props
  columns?: {
    desktop: number;
    tablet: number;
    mobile: number;
  };
  showDescriptions?: boolean;
  showStatuses?: boolean;
  showCTAs?: boolean;
  
  // Behavior Props
  onIntegrationClick?: (integration: Integration) => void;
  onCategoryFilter?: (category: string) => void;
  onLoadMore?: () => void;
  
  // Content Props
  title?: string;
  subtitle?: string;
  viewAllText?: string;
  viewAllHref?: string;
  
  // Style Props
  className?: string;
  variant?: 'homepage' | 'marketplace' | 'dashboard';
  
  // Animation Props
  enableAnimations?: boolean;
  staggerDelay?: number;
}

// Status Badge Component
const StatusBadge: React.FC<{ status: IntegrationStatus; progressPercentage?: number }> = ({ 
  status, 
  progressPercentage 
}) => {
  const { t } = useTranslation();
  
  const getStatusConfig = (status: IntegrationStatus) => {
    switch (status) {
      case 'ready':
        return { 
          icon: CheckCircle, 
          color: "bg-green-100 text-green-800 border-green-200", 
          text: t('integrations.status.ready', { ns: 'homepage' }) 
        };
      case 'coming':
        return { 
          icon: Clock, 
          color: "bg-blue-100 text-blue-800 border-blue-200", 
          text: t('integrations.status.coming', { ns: 'homepage' }) 
        };
      case 'development':
        return { 
          icon: AlertCircle, 
          color: "bg-yellow-100 text-yellow-800 border-yellow-200", 
          text: t('integrations.status.inDev', { ns: 'homepage' }) 
        };
      case 'beta':
        return { 
          icon: Sparkle, 
          color: "bg-purple-100 text-purple-800 border-purple-200", 
          text: t('integrations.status.beta', { ns: 'homepage' }) 
        };
      case 'concept':
        return { 
          icon: Lightbulb, 
          color: "bg-gray-100 text-gray-800 border-gray-200", 
          text: t('integrations.status.concept', { ns: 'homepage' }) 
        };
      default:
        return { 
          icon: Clock, 
          color: "bg-gray-100 text-gray-800 border-gray-200", 
          text: status 
        };
    }
  };

  const config = getStatusConfig(status);
  const IconComponent = config.icon;

  return (
    <div className="space-y-2">
      <Badge className={cn("text-xs font-medium px-2 py-1 border", config.color)}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.text}
      </Badge>
      {status === 'development' && progressPercentage && (
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div 
            className="bg-yellow-500 h-1.5 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      )}
    </div>
  );
};

const IntegrationsGrid: React.FC<IntegrationsGridProps> = ({
  integrations = [],
  maxDisplayed = 12,
  showAll = false,
  showCategories = false,
  enableFiltering = false,
  columns = { desktop: 3, tablet: 2, mobile: 1 },
  showDescriptions = true,
  showStatuses = true,
  showCTAs = true,
  title,
  subtitle,
  viewAllText,
  viewAllHref = "/integrations",
  variant = 'homepage',
  enableAnimations = true,
  staggerDelay = 0.1,
  onIntegrationClick,
  onCategoryFilter,
  onLoadMore,
  className = "",
  ...props
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const effectiveMaxDisplayed = showAll ? integrations.length : maxDisplayed;

  // State
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [displayedCount, setDisplayedCount] = useState(effectiveMaxDisplayed);

  useEffect(() => {
    setDisplayedCount(effectiveMaxDisplayed);
  }, [effectiveMaxDisplayed, selectedCategory]);

  // Default content with i18n
  const defaultTitle = title || t('integrations.choosePlatform', { ns: 'homepage' });
  const defaultSubtitle = subtitle || t('integrations.selectFromIntegrations', { ns: 'homepage' });
  const defaultViewAllText = viewAllText || t('integrations.viewAllIntegrations', { ns: 'homepage' });

  // Categories with dynamic counts
  const categories = {
    all: { label: t('integrations.categories.all', { ns: 'homepage' }), count: integrations.length },
    aiAssistants: { label: t('integrations.categories.aiAssistants', { ns: 'homepage' }), count: integrations.filter(i => i.category === 'aiAssistants').length },
    communication: { label: t('integrations.categories.communication', { ns: 'homepage' }), count: integrations.filter(i => i.category === 'communication').length },
    development: { label: t('integrations.categories.development', { ns: 'homepage' }), count: integrations.filter(i => i.category === 'development').length },
    automation: { label: t('integrations.categories.automation', { ns: 'homepage' }), count: integrations.filter(i => i.category === 'automation').length },
    productivity: { label: t('integrations.categories.productivity', { ns: 'homepage' }), count: integrations.filter(i => i.category === 'productivity').length },
    browser: { label: t('integrations.categories.browser', { ns: 'homepage' }), count: integrations.filter(i => i.category === 'browser').length },
    socialMedia: { label: t('integrations.categories.socialMedia', { ns: 'homepage' }), count: integrations.filter(i => i.category === 'socialMedia').length },
    mobile: { label: t('integrations.categories.mobile', { ns: 'homepage' }), count: integrations.filter(i => i.category === 'mobile').length },
  };

  const sortedIntegrations = useMemo(
    () => sortIntegrationsByStatus(integrations),
    [integrations]
  );

  // Filter integrations by category
  const filteredIntegrations = useMemo(() => {
    const byCategory =
      selectedCategory === 'all'
        ? sortedIntegrations
        : sortedIntegrations.filter(
            (integration) => integration.category === selectedCategory
          );
    return byCategory;
  }, [selectedCategory, sortedIntegrations]);

  const displayedIntegrations = filteredIntegrations.slice(0, displayedCount);
  const hasMore = filteredIntegrations.length > displayedCount;

  // Event handlers
  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    setDisplayedCount(effectiveMaxDisplayed);
    if (onCategoryFilter) {
      onCategoryFilter(category);
    }
  };

  const handleLoadMore = () => {
    setDisplayedCount((prev) =>
      Math.min(prev + maxDisplayed, filteredIntegrations.length)
    );
    if (onLoadMore) {
      onLoadMore();
    }
  };

  const handleIntegrationClick = (integration: Integration) => {
    if (integration.landingPath?.startsWith('mailto:')) {
      window.location.href = integration.landingPath;
      return;
    }
    if (onIntegrationClick) {
      onIntegrationClick(integration);
    } else if (integration.landingPath) {
      navigate(integration.landingPath);
    } else {
      navigate('/integrations');
    }
  };

  // Animation variants
  const gridVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    },
    hover: { 
      y: -4,
      transition: { duration: 0.2 }
    }
  };

  // Variant classes
  const variantClasses = {
    homepage: "space-y-6 md:space-y-8 px-4 md:px-8",
    marketplace: "space-y-4 md:space-y-6 px-4 md:px-6",
    dashboard: "space-y-3 md:space-y-4 px-2 md:px-4"
  };

  const gridColumnClasses = getGridColumnClasses(columns, variant);

  // Motion components
  const MotionDiv = enableAnimations ? motion.div : 'div';

  return (
    <section className={cn(variantClasses[variant], className)} {...props}>
      {/* Section Header */}
      <motion.div
        className="text-center space-y-6"
        initial={enableAnimations ? { opacity: 0, y: 30 } : undefined}
        animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
        transition={enableAnimations ? { duration: 0.7, delay: 0.2 } : undefined}
      >
        <motion.h2
          className="text-4xl md:text-5xl font-bold leading-tight"
          initial={enableAnimations ? { opacity: 0, scale: 0.9 } : undefined}
          animate={enableAnimations ? { opacity: 1, scale: 1 } : undefined}
          transition={enableAnimations ? { duration: 0.8, delay: 0.3 } : undefined}
        >
          <span className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent">
            {defaultTitle}
          </span>
        </motion.h2>
        <motion.p
          className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-light max-w-3xl mx-auto"
          initial={enableAnimations ? { opacity: 0, y: 20 } : undefined}
          animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
          transition={enableAnimations ? { duration: 0.6, delay: 0.5 } : undefined}
        >
          {defaultSubtitle}
        </motion.p>
      </motion.div>

      {/* Category Filter Tabs */}
      {showCategories && (
        <motion.div
          className="flex flex-wrap justify-center gap-3"
          initial={enableAnimations ? { opacity: 0, y: 20 } : undefined}
          animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
          transition={enableAnimations ? { duration: 0.6, delay: 0.7 } : undefined}
        >
          {Object.entries(categories).map(([key, category], index) => (
            <motion.div
              key={key}
              initial={enableAnimations ? { opacity: 0, scale: 0.8 } : undefined}
              animate={enableAnimations ? { opacity: 1, scale: 1 } : undefined}
              transition={enableAnimations ? { duration: 0.4, delay: 0.8 + index * 0.1 } : undefined}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant={selectedCategory === key ? "default" : "outline"}
                size="sm"
                onClick={() => handleCategoryClick(key)}
                className={`text-sm font-medium transition-all duration-300 ${
                  selectedCategory === key
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg'
                    : 'border-2 border-purple-200 hover:border-purple-400 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50'
                } px-6 py-2`}
              >
                {category.label}
                {enableFiltering && category.count > 0 && (
                  <Badge
                    variant="secondary"
                    className={`ml-2 text-xs ${
                      selectedCategory === key
                        ? 'bg-white/20 text-white'
                        : 'bg-purple-100 text-purple-700'
                    }`}
                  >
                    {category.count}
                  </Badge>
                )}
              </Button>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Integrations Grid */}
      <MotionDiv 
        variants={enableAnimations ? gridVariants : undefined}
        initial={enableAnimations ? "hidden" : undefined}
        animate={enableAnimations ? "visible" : undefined}
        className={cn(gridColumnClasses, "max-w-6xl mx-auto")}
      >
        {displayedIntegrations.map((integration, index) => (
          <motion.div
            key={integration.id}
            variants={enableAnimations ? cardVariants : undefined}
            className="group h-full"
          >
            <HoverLift liftAmount={6}>
              <Card
              className="h-full bg-white/80 backdrop-blur-sm border-2 border-gray-200 hover:border-purple-300 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden relative"
              onClick={() => handleIntegrationClick(integration)}
            >
              {/* Background gradient effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-indigo-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <CardHeader className="text-center pb-4 relative z-10">
                <div className="flex flex-col items-center space-y-4">
                  {/* Integration Icon */}
                  <motion.div
                    className="relative w-20 h-20 rounded-2xl flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-purple-50 group-hover:to-indigo-50 border-2 border-gray-200 group-hover:border-purple-300 shadow-sm group-hover:shadow-lg transition-all duration-500"
                    whileHover={{ rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 0.6 }}
                  >
                    <IntegrationIcon
                      icon={integration.icon}
                      className="w-10 h-10 text-gray-600 group-hover:text-purple-600 transition-colors duration-300"
                    />
                    {integration.isNew && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.5 + index * 0.1, type: "spring", stiffness: 500 }}
                        className="absolute -top-2 -right-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg border-2 border-white"
                      >
                        {t('integrations.newBadge', { ns: 'homepage' })}
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Title and Status */}
                  <div className="space-y-3">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    >
                      <GradientShift colors={["from-purple-600", "to-indigo-600"]}>
                        <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-purple-700 transition-colors duration-300">
                          {integration.name}
                        </CardTitle>
                      </GradientShift>
                    </motion.div>
                    {showStatuses && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                      >
                        <StatusBadge
                          status={integration.status}
                          progressPercentage={integration.progressPercentage}
                        />
                      </motion.div>
                    )}
                  </div>
                </div>
              </CardHeader>

              {showDescriptions && (
                <CardContent className="relative z-10">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                  >
                    <CardDescription className="text-center leading-relaxed mb-6 text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                      {integration.landingPath?.startsWith('mailto:') ? (
                        <a
                          href={integration.landingPath}
                          className="underline underline-offset-2"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {integration.description}
                        </a>
                      ) : (
                        integration.description
                      )}
                    </CardDescription>
                  </motion.div>

                  {showCTAs && (
                    <motion.div
                      className="text-center"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                    >
                      <PulseGlow color="purple" intensity={0.3}>
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full bg-white/90 hover:bg-white border-2 border-purple-200 hover:border-purple-400 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 shadow-sm hover:shadow-md transition-all duration-300 font-semibold"
                          >
                            {integration.status === 'ready'
                              ? t('integrations.cta.setup', { ns: 'homepage' })
                              : t('integrations.cta.notifyMe', { ns: 'homepage' })
                            }
                          </Button>
                        </motion.div>
                      </PulseGlow>
                    </motion.div>
                  )}
                </CardContent>
              )}
            </Card>
            </HoverLift>
          </motion.div>
        ))}
      </MotionDiv>

      {/* Load More / View All */}
      <motion.div
        className="text-center space-y-6"
        initial={enableAnimations ? { opacity: 0, y: 20 } : undefined}
        animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
        transition={enableAnimations ? { duration: 0.5, delay: 1.2 } : undefined}
      >
        {hasMore && (
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              variant="outline"
              onClick={handleLoadMore}
              className="px-8 py-3 bg-white/90 backdrop-blur-sm border-2 border-purple-200 hover:border-purple-400 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold"
            >
              {t('integrations.loadMoreRemaining', {
                ns: 'homepage',
                count: filteredIntegrations.length - displayedCount,
              })}
            </Button>
          </motion.div>
        )}

        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            variant="outline"
            size="lg"
            onClick={() => window.location.href = viewAllHref}
            className="h-14 px-10 text-lg font-bold bg-white/90 backdrop-blur-sm border-2 border-purple-300 hover:border-purple-400 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {defaultViewAllText}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default IntegrationsGrid;
export type { IntegrationsGridProps, Integration, IntegrationCategory, IntegrationStatus };
